import { useApi, apiPost, Cargando, ErrorApi, Vacio, BotonAccion, Modal, formatU, formatFecha, confirmar } from '../shared/ui.tsx';
import { exportarCSV, type ColumnaTabla } from '../shared/exportar.ts';
import { horasEntreHHmm, cruzaMedianoche, formatoRango } from '../shared/horas.ts';
import { useState, type ReactNode, type FormEvent } from 'react';

type PersonaConv = {
  convocatoria_id: string;
  persona_nombre: string;
  persona_apellido: string;
  categoria_nombre: string;
  estado: string;
  hora_inicio: string | null; // horario propio del puesto (null = el del evento)
  hora_fin: string | null;
  puesto_id: string | null;
  instrucciones: string | null;        // solo para esta persona
  instrucciones_turno: string | null;  // para todos los del turno
};

type Fila = {
  id: string; fecha: string; hora_inicio: string; hora_fin: string; estado: string;
  salon_nombre: string; cliente_nombre: string; descripcion: string | null;
  convocatorias_total: number; convocatorias_aceptadas: number;
  convocatorias_pendientes: number; convocatorias_rechazadas: number;
  personas: PersonaConv[];
};

function Icono({ d, className = 'h-4 w-4' }: { d: string; className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  );
}

const ICONOS = {
  calendario: 'M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5',
  reloj: 'M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z',
  lugar: 'M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z',
  recargar: 'M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99',
  descargar: 'M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3',
  cubiertos: 'M15.75 10.5V6a3.75 3.75 0 1 0-7.5 0v4.5m11.356-1.993 1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 0 1-1.12-1.243l1.264-12A1.125 1.125 0 0 1 5.513 7.5h12.974c.576 0 1.059.435 1.119 1.007Z',
  edificio: 'M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75',
  ojo: 'M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z',
  prohibido: 'M18.364 18.364A9 9 0 0 0 5.636 5.636m12.728 12.728A9 9 0 0 1 5.636 5.636m12.728 12.728L5.636 5.636',
};

function chipEstado(estado: string): { texto: string; clase: string } {
  switch (estado) {
    case 'confirmada': return { texto: 'CONFIRMADA', clase: 'bg-estado-ok/15 text-estado-ok' };
    case 'cancelada': return { texto: 'CANCELADA', clase: 'bg-red-100 text-estado-error' };
    case 'en_asignacion': return { texto: 'EN ASIGNACIÓN', clase: 'bg-amber-100 text-amber-700' };
    case 'finalizada': return { texto: 'FINALIZADA', clase: 'bg-superficie-200 text-neutro-500' };
    default: return { texto: estado.toUpperCase().replace('_', ' '), clase: 'bg-superficie-200 text-neutro-500' };
  }
}

function idCorto(id: string): string {
  return `#${id.slice(0, 8).toUpperCase()}`;
}

const COLUMNAS_EXPORT: ColumnaTabla[] = [
  { clave: 'fecha', etiqueta: 'Fecha', formato: 'fecha' },
  { clave: 'cliente_nombre', etiqueta: 'Cliente' },
  { clave: 'salon_nombre', etiqueta: 'Salón' },
  { clave: 'hora_inicio', etiqueta: 'Desde' },
  { clave: 'hora_fin', etiqueta: 'Hasta' },
  { clave: 'estado', etiqueta: 'Estado' },
  { clave: 'convocatorias_aceptadas', etiqueta: 'Confirmadas', formato: 'numero' },
  { clave: 'convocatorias_total', etiqueta: 'Pedidas', formato: 'numero' },
  { clave: 'personas_str', etiqueta: 'Personas' },
  { clave: 'id', etiqueta: 'ID' },
];

const FILTROS_ESTADO = [
  { valor: '', etiqueta: 'Todas' },
  { valor: 'enviada', etiqueta: 'Enviada' },
  { valor: 'en_asignacion', etiqueta: 'En asignación' },
  { valor: 'confirmada', etiqueta: 'Confirmada' },
  { valor: 'finalizada', etiqueta: 'Finalizada' },
  { valor: 'cancelada', etiqueta: 'Cancelada' },
];

type FiltroTiempo = 'todas' | 'hoy' | 'semana' | 'proxima';

const FILTROS_TIEMPO: Array<{ valor: FiltroTiempo; etiqueta: string }> = [
  { valor: 'hoy', etiqueta: 'Hoy' },
  { valor: 'semana', etiqueta: 'Esta semana' },
  { valor: 'proxima', etiqueta: 'Semana que viene' },
  { valor: 'todas', etiqueta: 'Todas las fechas' },
];

function lunesActual(): Date {
  const hoy = new Date();
  const dow = hoy.getDay() === 0 ? 7 : hoy.getDay();
  return new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() - (dow - 1));
}

function pasaFiltroTiempo(fecha: string, f: FiltroTiempo): boolean {
  if (f === 'todas') return true;
  const d = new Date(`${fecha}T00:00:00`);
  const ahora = new Date();
  const hoy0 = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
  if (f === 'hoy') return d.getTime() === hoy0.getTime();
  const lun = lunesActual();
  const lunProx = new Date(lun.getFullYear(), lun.getMonth(), lun.getDate() + 7);
  const lunSig = new Date(lun.getFullYear(), lun.getMonth(), lun.getDate() + 14);
  if (f === 'semana') return d >= lun && d < lunProx;
  return d >= lunProx && d < lunSig;
}

export function SolicitudesAdminPage(): ReactNode {
  const { datos, cargando, error, recargar } = useApi<Fila[]>('/api/solicitudes');
  const clientes = useApi<Array<{ id: string; nombre: string }>>('/api/clientes');
  const [filtro, setFiltro] = useState<string>('');
  // Arranca en "Esta semana": el admin mira de un vistazo si la semana está confirmada.
  const [filtroTiempo, setFiltroTiempo] = useState<FiltroTiempo>('semana');
  const [filtroCliente, setFiltroCliente] = useState<string>('');
  const [busqueda, setBusqueda] = useState<string>('');

  const q = busqueda.trim().replace(/^#/, '').toLowerCase();
  const filtrados = (datos ?? []).filter((s) =>
    (!filtro || s.estado === filtro)
    && pasaFiltroTiempo(s.fecha, filtroTiempo)
    && (!filtroCliente || s.cliente_nombre === filtroCliente)
    && (!q
      || s.id.toLowerCase().includes(q)
      || s.cliente_nombre.toLowerCase().includes(q)
      || s.salon_nombre.toLowerCase().includes(q)
      || s.personas.some((p) => `${p.persona_nombre} ${p.persona_apellido}`.toLowerCase().includes(q))));

  function exportar() {
    const filas = filtrados.map((s) => ({
      ...s,
      estado: s.estado.replace('_', ' '),
      personas_str: s.personas
        .filter((p) => p.estado === 'aceptada')
        .map((p) => `${p.persona_apellido}, ${p.persona_nombre} (${p.categoria_nombre})`)
        .join(' · '),
    }));
    exportarCSV(filas, COLUMNAS_EXPORT, `solicitudes-${new Date().toISOString().slice(0, 10)}.csv`);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-extrabold text-neutro-800">Solicitudes</h2>
        <div className="flex gap-2">
          <button
            title="Recargar"
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-contorno-var bg-white text-neutro-600 hover:bg-superficie-100 transition-colors"
            onClick={recargar}
          >
            <Icono d={ICONOS.recargar} className="h-4.5 w-4.5" />
          </button>
          <button
            title="Exportar a Excel"
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-contorno-var bg-white text-neutro-600 hover:bg-superficie-100 transition-colors"
            onClick={exportar}
          >
            <Icono d={ICONOS.descargar} className="h-4.5 w-4.5" />
          </button>
        </div>
      </div>

      {/* Filtros + buscador */}
      <div className="rounded-2xl border border-contorno-var bg-white p-3 space-y-2">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {FILTROS_ESTADO.map((e) => (
            <button
              key={e.valor || 'todas'}
              className={`rounded-full px-3.5 py-1.5 font-semibold transition-colors ${
                filtro === e.valor ? 'bg-primario-400 text-white shadow-sm' : 'border border-contorno-var bg-white text-neutro-600 hover:bg-superficie-100'
              }`}
              onClick={() => setFiltro(e.valor)}
            >
              {e.etiqueta}
            </button>
          ))}
          <input
            className="campo ml-auto w-full sm:w-56"
            type="search"
            placeholder="Buscar por nombre o ID…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
          <select className="campo w-full sm:w-52" value={filtroCliente} onChange={(e) => setFiltroCliente(e.target.value)}>
            <option value="">Todos los locales</option>
            {(clientes.datos ?? []).map((c) => <option key={c.id} value={c.nombre}>{c.nombre}</option>)}
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-2 border-t border-superficie-200 pt-2 text-sm">
          <span className="text-xs font-bold uppercase tracking-wide text-neutro-400">Fecha:</span>
          {FILTROS_TIEMPO.map((t) => (
            <button
              key={t.valor}
              className={`rounded-full px-3.5 py-1.5 font-semibold transition-colors ${
                filtroTiempo === t.valor ? 'bg-neutro-800 text-white shadow-sm' : 'border border-contorno-var bg-white text-neutro-600 hover:bg-superficie-100'
              }`}
              onClick={() => setFiltroTiempo(t.valor)}
            >
              {t.etiqueta}
            </button>
          ))}
        </div>
      </div>

      {cargando ? <Cargando /> : error ? <ErrorApi mensaje={error} onReintentar={recargar} /> : filtrados.length > 0 ? (
        <ul className="space-y-3">
          {filtrados.map((s) => <FilaSolicitud key={s.id} sol={s} onCambio={recargar} />)}
        </ul>
      ) : <Vacio texto="No hay solicitudes en este filtro." />}
    </div>
  );
}

function FilaSolicitud({ sol, onCambio }: { sol: Fila; onCambio: () => void }): ReactNode {
  const [mostrarConv, setMostrarConv] = useState(false);
  const [mostrarTurnoConv, setMostrarTurnoConv] = useState<string | null>(null);
  const [instruccionesDe, setInstruccionesDe] = useState<PersonaConv | null>(null);
  const [reasignando, setReasignando] = useState<string | null>(null);

  const chip = chipEstado(sol.estado);
  const cancelada = sol.estado === 'cancelada';
  const aceptadas = sol.personas.filter((p) => p.estado === 'aceptada');
  const hoy = new Date().toISOString().slice(0, 10);
  const esFutura = sol.fecha >= hoy;

  async function asignar() {
    if (!confirmar('¿Lanzar asignación automática?')) return;
    try {
      const r = await apiPost<{ enviadas: number; faltantes: number }>(`/api/solicitudes/${sol.id}/asignar`, undefined, 'POST');
      if (r.enviadas === 0 && r.faltantes > 0) {
        alert(`No se encontraron candidatos disponibles para ${r.faltantes} puesto(s).`);
      } else if (r.faltantes > 0) {
        alert(`Se enviaron ${r.enviadas} convocatoria(s). Quedan ${r.faltantes} puesto(s) sin candidatos.`);
      }
      onCambio();
    }
    catch (e) { alert(e instanceof Error ? e.message : 'Error'); }
  }

  async function cancelar() {
    if (!confirmar('¿Cancelar solicitud?')) return;
    try { await apiPost(`/api/solicitudes/${sol.id}/cancelar`, undefined, 'POST'); onCambio(); }
    catch (e) { alert(e instanceof Error ? e.message : 'Error'); }
  }

  async function reasignar(p: PersonaConv) {
    if (!confirmar(`¿Sacar a ${p.persona_nombre} ${p.persona_apellido} de este servicio y reconvocar automáticamente al siguiente? Se le avisa a la persona.`)) return;
    setReasignando(p.convocatoria_id);
    try {
      await apiPost(`/api/convocatorias/${p.convocatoria_id}/reasignar`, undefined, 'POST');
      onCambio();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Error');
    } finally {
      setReasignando(null);
    }
  }

  return (
    <li className={`rounded-2xl bg-white shadow-sm border border-contorno-var overflow-hidden ${cancelada ? 'opacity-60' : ''}`}>
      <div className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${cancelada ? 'bg-superficie-200 text-neutro-400' : 'bg-primario-100 text-primario-400'}`}>
              <Icono d={cancelada ? ICONOS.prohibido : ICONOS.cubiertos} className="h-5 w-5" />
            </div>
            <div>
              <p className="flex flex-wrap items-center gap-2">
                <span className="rounded bg-primario-100 px-1.5 py-0.5 text-[10px] font-extrabold tracking-wide text-primario-500">CLIENTE</span>
                <span className="text-base font-extrabold text-neutro-800">{sol.cliente_nombre}</span>
              </p>
              <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-neutro-500">
                <span className="flex items-center gap-1"><Icono d={ICONOS.calendario} className="h-3.5 w-3.5" />{formatFecha(sol.fecha)}</span>
                <span className="flex items-center gap-1"><Icono d={ICONOS.reloj} className="h-3.5 w-3.5" />{sol.hora_inicio} — {sol.hora_fin}</span>
                <span className="flex items-center gap-1"><Icono d={ICONOS.lugar} className="h-3.5 w-3.5" />{sol.salon_nombre}</span>
              </p>
              {sol.descripcion && <p className="mt-1 text-xs text-neutro-400">{sol.descripcion}</p>}
            </div>
          </div>
          <span className={`shrink-0 rounded-full px-3 py-1 text-[11px] font-extrabold tracking-wide ${chip.clase}`}>{chip.texto}</span>
        </div>

        {sol.estado === 'en_asignacion' && (
          <p className="mt-2 text-xs font-semibold text-amber-700">
            {sol.convocatorias_aceptadas}/{sol.convocatorias_total} confirmadas
            {sol.convocatorias_pendientes > 0 && ` · ${sol.convocatorias_pendientes} esperando respuesta`}
            {sol.convocatorias_rechazadas > 0 && ` · ${sol.convocatorias_rechazadas} rechazadas`}
          </p>
        )}

        {/* Convocatorias aceptadas, desplegadas en la tarjeta */}
        {!cancelada && aceptadas.length > 0 && (
          <div className="mt-4 border-t border-superficie-200 pt-3">
            <p className="text-sm font-bold text-neutro-800">Convocatorias · {sol.salon_nombre}</p>
            <ul className="mt-1">
              {aceptadas.map((p) => (
                <li key={p.convocatoria_id} className="flex flex-wrap items-center justify-between gap-2 border-b border-superficie-200 py-2 last:border-0 text-sm">
                  <span className="text-neutro-700">
                    {p.persona_apellido}, {p.persona_nombre} · {p.categoria_nombre}
                    {p.hora_inicio && p.hora_fin && (
                      <span className="ml-1.5 text-xs font-semibold text-primario-400">{formatoRango(p.hora_inicio, p.hora_fin)}</span>
                    )}
                  </span>
                  <span className="flex items-center gap-3">
                    <span className="font-bold text-estado-ok">Aceptada</span>
                    {esFutura && (
                      <button
                        className="font-semibold text-primario-400 hover:underline disabled:opacity-50"
                        disabled={reasignando === p.convocatoria_id}
                        onClick={() => void reasignar(p)}
                      >
                        {reasignando === p.convocatoria_id ? 'Reasignando…' : 'Reasignar'}
                      </button>
                    )}
                    <button
                      className="font-semibold text-primario-400 hover:underline"
                      onClick={() => setInstruccionesDe(p)}
                    >
                      {p.instrucciones || p.instrucciones_turno ? '✎ Instrucciones' : 'Instrucciones'}
                    </button>
                    <button
                      className="font-semibold text-primario-400 hover:underline"
                      onClick={() => setMostrarTurnoConv(p.convocatoria_id)}
                    >
                      Cargar turno
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs font-medium text-neutro-300">ID: {idCorto(sol.id)}</span>
          <div className="flex flex-wrap gap-2">
            {cancelada ? (
              <BotonAccion variante="secundario" onClick={() => setMostrarConv(true)}>Ver detalles históricos</BotonAccion>
            ) : (
              <>
                <BotonAccion onClick={() => setMostrarConv(true)} className="bg-primario-400 text-white">
                  <Icono d={ICONOS.ojo} className="h-4 w-4" /> Ver convocatorias
                </BotonAccion>
                {sol.estado === 'enviada' && (
                  <BotonAccion variante="secundario" onClick={asignar}>Lanzar asignación</BotonAccion>
                )}
                {sol.estado === 'en_asignacion' && (
                  sol.convocatorias_aceptadas + sol.convocatorias_pendientes < sol.convocatorias_total ? (
                    <BotonAccion variante="secundario" onClick={asignar}>
                      Relanzar (faltan {sol.convocatorias_total - sol.convocatorias_aceptadas - sol.convocatorias_pendientes})
                    </BotonAccion>
                  ) : (
                    <span className="rounded-lg bg-superficie-200 px-3 py-1.5 text-sm text-neutro-500">
                      ⏳ Esperando respuestas
                    </span>
                  )
                )}
                {sol.estado !== 'finalizada' && (
                  <BotonAccion variante="peligro" onClick={cancelar}>Cancelar</BotonAccion>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {mostrarConv && <ModalConvocatorias solId={sol.id} estado={sol.estado} salonNombre={sol.salon_nombre} onCerrar={() => setMostrarConv(false)} onCargarTurno={(cid) => setMostrarTurnoConv(cid)} />}
      {mostrarTurnoConv && <ModalTurno convocatoriaId={mostrarTurnoConv} onClose={() => setMostrarTurnoConv(null)} />}
      {instruccionesDe && (
        <ModalInstrucciones
          persona={instruccionesDe}
          cantidadTurno={sol.personas.filter((x) => x.puesto_id && x.puesto_id === instruccionesDe.puesto_id).length}
          onCerrar={() => setInstruccionesDe(null)}
          onGuardado={() => { setInstruccionesDe(null); onCambio(); }}
        />
      )}
    </li>
  );
}

type PersonaMin = { persona_id: string; nombre?: string; apellido?: string };
type CandidatosPuesto = {
  categoria_id: string; categoria_nombre: string; cantidad: number;
  seleccionadas: PersonaMin[]; convocara: PersonaMin[]; lista_espera: PersonaMin[];
};

function ModalConvocatorias({ solId, estado, salonNombre, onCerrar, onCargarTurno }: { solId: string; estado: string; salonNombre: string; onCerrar: () => void; onCargarTurno: (cid: string) => void }): ReactNode {
  const sinLanzar = estado === 'enviada';
  const { datos, cargando, error, recargar } = useApi<Array<{
    id: string; persona_nombre: string; persona_apellido: string; categoria_nombre: string; estado: string;
  }>>(`/api/solicitudes/${solId}/convocatorias`);
  const candidatos = useApi<CandidatosPuesto[]>(sinLanzar ? `/api/solicitudes/${solId}/candidatos` : null);

  return (
    <>
      <Modal abierto={true} titulo={sinLanzar ? `Se convocará a · ${salonNombre}` : `Convocatorias · ${salonNombre}`} onCerrar={onCerrar}>
        {sinLanzar ? (
          candidatos.cargando ? <Cargando /> : candidatos.error ? <ErrorApi mensaje={candidatos.error} onReintentar={candidatos.recargar} /> : candidatos.datos && candidatos.datos.length > 0 ? (
            <div className="space-y-4 text-sm">
              <p className="text-xs text-neutro-500">Todavía no se lanzó la asignación. Esto es lo que va a pasar al lanzarla:</p>
              {candidatos.datos.map((p) => (
                <div key={p.categoria_id}>
                  <p className="font-semibold text-neutro-800">{p.categoria_nombre} · {p.cantidad} puesto(s)</p>
                  <ul className="mt-1 space-y-1">
                    {p.seleccionadas.map((per) => (
                      <li key={per.persona_id} className="flex items-center justify-between border-b border-contorno-var py-1">
                        <span>{per.apellido}, {per.nombre}</span>
                        <span className="rounded-full bg-primario-400/10 px-2 py-0.5 text-xs font-medium text-primario-400">Elegida por el cliente</span>
                      </li>
                    ))}
                    {p.convocara.map((per) => (
                      <li key={per.persona_id} className="flex items-center justify-between border-b border-contorno-var py-1">
                        <span>{per.apellido}, {per.nombre}</span>
                        <span className="text-xs text-neutro-500">Se convocará</span>
                      </li>
                    ))}
                    {p.seleccionadas.length + p.convocara.length < p.cantidad && (
                      <li className="py-1 text-xs text-estado-error">
                        ⚠ Sin candidatos disponibles para {p.cantidad - p.seleccionadas.length - p.convocara.length} puesto(s)
                      </li>
                    )}
                  </ul>
                  {p.lista_espera.length > 0 && (
                    <p className="mt-1 text-xs text-neutro-400">
                      En espera: {p.lista_espera.map((per) => `${per.apellido}, ${per.nombre}`).join(' · ')}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : <Vacio texto="La solicitud no tiene puestos." />
        ) : cargando ? <Cargando /> : error ? <ErrorApi mensaje={error} onReintentar={recargar} /> : datos && datos.length > 0 ? (
          <ul className="space-y-1.5 text-sm">
            {datos.map((c) => {
              const color = c.estado === 'aceptada' ? 'text-estado-ok' : (c.estado === 'rechazada' || c.estado === 'vencida') ? 'text-estado-error' : 'text-estado-pendiente';
              return (
                <li key={c.id} className="flex items-center justify-between border-b border-contorno-var py-1.5">
                  <span>{c.persona_apellido}, {c.persona_nombre} · {c.categoria_nombre}</span>
                  <div className="flex items-center gap-3">
                    <span className={`font-medium capitalize ${color}`}>{c.estado.replace('_', ' ')}</span>
                    {c.estado === 'aceptada' && (
                      <button className="text-xs text-primario-400 hover:underline" onClick={() => onCargarTurno(c.id)}>Cargar turno</button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        ) : <Vacio texto="No hay convocatorias." />}
      </Modal>
    </>
  );
}

function ModalTurno({ convocatoriaId, onClose }: { convocatoriaId: string; onClose: () => void }): ReactNode {
  const [inicio, setInicio] = useState('18:00');
  const [fin, setFin] = useState('23:00');
  const [esFeriado, setEsFeriado] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [resultado, setResultado] = useState<{ pago_calculado: number; cobro_calculado: number } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function guardar(e: FormEvent) {
    e.preventDefault();
    setErr(null); setGuardando(true);
    try {
      const r = await apiPost<{ pago_calculado: number; cobro_calculado: number }>(`/api/convocatorias/${convocatoriaId}/turno`, { hora_inicio_real: inicio, hora_fin_real: fin, es_feriado: esFeriado });
      setResultado({ pago_calculado: r.pago_calculado, cobro_calculado: r.cobro_calculado });
    } catch (e2) { setErr(e2 instanceof Error ? e2.message : 'Error'); }
    finally { setGuardando(false); }
  }

  return (
    <Modal abierto={true} titulo="Cargar turno trabajado" onCerrar={onClose}>
      <form onSubmit={guardar} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-neutro-800">Hora inicio real*</span>
            <input className="campo" type="time" required value={inicio} onChange={(e) => setInicio(e.target.value)} />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-neutro-800">Hora fin real*</span>
            <input className="campo" type="time" required value={fin} onChange={(e) => setFin(e.target.value)} />
          </label>
        </div>
        {inicio && fin && inicio !== fin && (
          <p className="text-sm text-neutro-600">
            Total: <span className="font-semibold">{horasEntreHHmm(inicio, fin).toLocaleString('es-UY')} h</span>
            {cruzaMedianoche(inicio, fin) && (
              <span className="ml-2 rounded-full bg-primario-100 px-2 py-0.5 text-xs font-semibold text-primario-500">⚠ Termina al día siguiente</span>
            )}
          </p>
        )}
        <label className="flex items-center gap-2 text-sm text-neutro-800">
          <input type="checkbox" checked={esFeriado} onChange={(e) => setEsFeriado(e.target.checked)} />
          Es feriado
        </label>
        {err && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-estado-error">{err}</p>}
        {resultado && (
          <div className="rounded-lg bg-superficie-100 p-3 text-sm">
            <p>Pago al personal: <span className="font-bold">{formatU(resultado.pago_calculado)}</span></p>
            <p>Cobro al cliente: <span className="font-bold">{formatU(resultado.cobro_calculado)}</span></p>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <BotonAccion type="button" variante="secundario" onClick={onClose}>Cerrar</BotonAccion>
          <BotonAccion type="submit" disabled={guardando}>{guardando ? 'Calculando…' : 'Calcular'}</BotonAccion>
        </div>
      </form>
    </Modal>
  );
}


/**
 * Instrucciones de un servicio, en dos niveles: las del turno las ven todos
 * los de ese turno; las individuales, solo esa persona.
 */
function ModalInstrucciones({ persona, cantidadTurno, onCerrar, onGuardado }: {
  persona: PersonaConv;
  cantidadTurno: number;
  onCerrar: () => void;
  onGuardado: () => void;
}): ReactNode {
  const [turno, setTurno] = useState(persona.instrucciones_turno ?? '');
  const [individual, setIndividual] = useState(persona.instrucciones ?? '');
  const [guardando, setGuardando] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const cambioTurno = turno.trim() !== (persona.instrucciones_turno ?? '').trim();
  const cambioIndividual = individual.trim() !== (persona.instrucciones ?? '').trim();

  async function guardar(e: FormEvent) {
    e.preventDefault();
    setGuardando(true);
    setErr(null);
    try {
      // Solo se manda lo que cambió, para no pisar el otro nivel al pedo.
      if (cambioTurno && persona.puesto_id) {
        await apiPost(`/api/puestos/${persona.puesto_id}/instrucciones`, { instrucciones: turno }, 'PUT');
      }
      if (cambioIndividual) {
        await apiPost(`/api/convocatorias/${persona.convocatoria_id}/instrucciones`, { instrucciones: individual }, 'PUT');
      }
      onGuardado();
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Error');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Modal abierto={true} titulo="Instrucciones del servicio" onCerrar={onCerrar}>
      <form onSubmit={guardar} className="space-y-4 text-sm">
        <label className="block">
          <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-neutro-500">
            Para todo el turno · {persona.categoria_nombre}
            {persona.hora_inicio && persona.hora_fin && ` ${formatoRango(persona.hora_inicio, persona.hora_fin)}`}
          </span>
          <textarea
            className="campo"
            rows={3}
            maxLength={1000}
            value={turno}
            disabled={!persona.puesto_id}
            onChange={(e) => setTurno(e.target.value)}
            placeholder="Ej: camisa blanca y moño. Entrada por el portón de servicio."
          />
          <span className="mt-1 block text-xs text-neutro-400">
            {persona.puesto_id
              ? cantidadTurno === 1
                ? 'Por ahora lo ve 1 sola persona: la de este turno.'
                : `Lo ven las ${cantidadTurno} personas de este turno.`
              : 'Esta convocatoria es de antes de los turnos con horario propio, así que solo admite instrucciones individuales.'}
          </span>
        </label>

        <label className="block">
          <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-neutro-500">
            Solo para {persona.persona_apellido}, {persona.persona_nombre}
          </span>
          <textarea
            className="campo"
            rows={3}
            maxLength={1000}
            value={individual}
            onChange={(e) => setIndividual(e.target.value)}
            placeholder="Ej: estás en la barra del Hoyo 10. Llegá 19:30 para el armado."
          />
        </label>

        {err && <p className="rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-700">{err}</p>}

        <div className="flex justify-end gap-2">
          <BotonAccion type="button" variante="secundario" onClick={onCerrar} disabled={guardando}>Cancelar</BotonAccion>
          <BotonAccion type="submit" disabled={guardando || (!cambioTurno && !cambioIndividual)} className="bg-primario-400 text-white">
            {guardando ? 'Guardando…' : 'Guardar'}
          </BotonAccion>
        </div>
      </form>
    </Modal>
  );
}
